import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const NOTIFICATION_TYPES = [
  { value: 'newsletter', labelKey: 'site_email_notify_newsletter', fallback: 'Suscripciones al newsletter' },
  { value: 'career', labelKey: 'site_email_notify_career', fallback: 'Postulaciones' },
  { value: 'order', labelKey: 'site_email_notify_order', fallback: 'Ventas web' },
  { value: 'contact', labelKey: 'site_email_notify_contact', fallback: 'Mensajes de contacto' }
];

const DEFAULT_TYPES = NOTIFICATION_TYPES.map((type) => type.value);

const NotificationEmailTab = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    enabled: false,
    email: '',
    types: DEFAULT_TYPES
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadConfig = async () => {
      try {
        const config = await SitesService.getSiteNotificationEmailConfig();
        if (!mounted) return;
        setForm({
          enabled: config?.enabled === true,
          email: config?.email || '',
          types: Array.isArray(config?.types) && config.types.length > 0 ? config.types : DEFAULT_TYPES
        });
      } catch (error) {
        console.error('[NotificationEmailTab] Error loading config:', error);
        SnackbarUtils.error(t('error_loading_notifications') || 'Error al cargar notificaciones');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadConfig();
    return () => {
      mounted = false;
    };
  }, [t]);

  const toggleType = (type) => {
    setForm((current) => {
      const hasType = current.types.includes(type);
      return {
        ...current,
        types: hasType
          ? current.types.filter((item) => item !== type)
          : [...current.types, type]
      };
    });
  };

  const handleSave = async () => {
    const email = form.email.trim();
    if (form.enabled && !email) {
      SnackbarUtils.error(t('site_email_required') || 'Ingresá un email para recibir las notificaciones.');
      return;
    }
    if (form.enabled && form.types.length === 0) {
      SnackbarUtils.error(t('site_email_types_required') || 'Seleccioná al menos un tipo de notificación.');
      return;
    }

    try {
      setSaving(true);
      const updated = await SitesService.updateSiteNotificationEmailConfig({
        enabled: form.enabled,
        email,
        types: form.types
      });
      setForm({
        enabled: updated?.enabled === true,
        email: updated?.email || email,
        types: Array.isArray(updated?.types) && updated.types.length > 0 ? updated.types : form.types
      });
      SnackbarUtils.success(t('site_config_saved_successfully') || 'Configuración guardada exitosamente');
    } catch (error) {
      console.error('[NotificationEmailTab] Error saving config:', error);
      SnackbarUtils.error(t('site_config_save_error') || 'No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>{t('loading') || 'Cargando...'}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
                color="primary"
              />
            }
            label={t('site_email_notifications_enabled') || 'Enviar notificaciones web por email'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            type="email"
            label={t('site_email_notifications_to') || 'Email de notificación'}
            value={form.email}
            disabled={!form.enabled}
            required={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('site_email_notifications_types') || 'Recibir avisos por'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
            {NOTIFICATION_TYPES.map((type) => (
              <FormControlLabel
                key={type.value}
                control={
                  <Checkbox
                    checked={form.types.includes(type.value)}
                    disabled={!form.enabled}
                    onChange={() => toggleType(type.value)}
                  />
                }
                label={t(type.labelKey) || type.fallback}
              />
            ))}
          </Box>
        </Grid>

        {form.enabled && (
          <Grid item xs={12}>
            <Alert severity="info">
              {t('site_email_notifications_help') || 'Se enviará un correo cada vez que la web genere una notificación de los tipos seleccionados.'}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default NotificationEmailTab;

